/**
 * The assembled Bulgarian Belot GameModule.
 *
 * It is French Belote's neighbour, not a variant of it: the auction, the No
 * Trumps and All Trumps contracts, the way declarations compare and the
 * divide-by-ten scoring are all its own, so it is a module of its own and the
 * French one is untouched. The card tables are shared in spirit and restated
 * in `rules.ts`.
 *
 * The player count is a config option: `table(config)` seats four in
 * partnerships or two head to head, and the 2-player game is dealt from a
 * 24-card pack whose last eight cards are never dealt — they stay in the
 * committed permutation's stock, hidden from everyone.
 */
import { rankOf, suitOf } from '../../core/cards.js';
import { individualTable, partnershipTable } from '../../core/seats.js';
import { BELOT_TRICKS_PER_HAND, DEFAULT_BELOT_CONFIG, validateBelotConfig } from './config.js';
import { belotAuction } from './auction.js';
import { announcementsOf, belotCardsDealt, belotSuits, resolveBelotDeclarations } from './declarations.js';
import { belotCurrentTrick, belotDealPlan, belotDeck, belotLegalPlays, belotRanking, belotVisibility, } from './rules.js';
import { belotMatchEnd, belotScoreHand } from './scoring.js';
const INIT_EXTRA = { announcements: null, belots: [] };
const TABLE_4 = partnershipTable(4);
const TABLE_2 = individualTable(2);
export function belotTable(config) {
    return config.players === 2 ? TABLE_2 : TABLE_4;
}
/** Rules §2: after the auction, three more cards each from the dealer's
 *  right, from the stock of the permutation committed before the deal. */
export function completeBelotDeal(hand, table) {
    const stock = [...hand.secret.stock];
    const holdings = hand.secret.holdings.map((h) => [...h]);
    for (let i = 1; i <= table.seatCount; i++) {
        holdings[(hand.dealer + i) % table.seatCount].push(...stock.splice(0, 3));
    }
    return holdings;
}
export const belot = {
    // Bulgarian Belot is dealt and played anticlockwise, as French Belote is.
    meta: { id: 'belot', name: 'Bulgarian Belot', rotation: 'anticlockwise' },
    defaultConfig: DEFAULT_BELOT_CONFIG,
    validateConfig: validateBelotConfig,
    table: belotTable,
    tricksPerHand: () => BELOT_TRICKS_PER_HAND,
    deck: belotDeck,
    dealPlan: belotDealPlan,
    auction: belotAuction,
    ranking(hand) {
        return belotRanking(hand.contract?.mode ?? null);
    },
    legalPlays(hand, seat, table) {
        const mode = hand.contract?.mode;
        if (!mode)
            return [];
        return belotLegalPlays(hand.secret.holdings[seat] ?? [], seat, belotCurrentTrick(hand), mode, table);
    },
    initHandExtra: () => INIT_EXTRA,
    initMatchExtra: () => ({ hanging: 0, lastHand: null }),
    onContract(hand, table) {
        return {
            extra: INIT_EXTRA,
            pending: null,
            activeSeats: Array.from({ length: table.seatCount }, (_, i) => i),
            holdings: completeBelotDeal(hand, table),
            // The player to the dealer's right leads, whoever bid.
            leader: (hand.dealer + 1) % table.seatCount,
        };
    },
    applyDecision() {
        return { ok: false, error: 'Bulgarian Belot has no decisions' };
    },
    /**
     * Belot is announced with the first of a King and Queen held together
     * (rules §6.5): the trump suit's in a suit contract, any suit's in All
     * Trumps — but there only when the card is led or follows suit, never when
     * it is thrown away. The hook sees the card already on the table and gone
     * from the hand, so the other card still being held is the test.
     */
    onCardPlayed(hand, seat, card, _nominate) {
        const mode = hand.contract?.mode;
        if (!mode)
            return hand.extra;
        const suit = suitOf(card);
        const rank = rankOf(card);
        if (!suit || (rank !== 12 && rank !== 13) || !belotSuits(mode).includes(suit))
            return hand.extra;
        if (mode === 'AT') {
            const trick = belotCurrentTrick(hand);
            const led = trick.plays.length === 1 || trick.ledSuit === suit;
            if (!led)
                return hand.extra;
        }
        const other = `${rank === 12 ? 13 : 12}${suit}`;
        if (!hand.secret.holdings[seat]?.includes(other))
            return hand.extra;
        return { ...hand.extra, belots: [...hand.extra.belots, { seat, suit }] };
    },
    /** Declarations are announced in trick 1 and compared once it is complete. */
    onTrickComplete(hand, config, table) {
        const mode = hand.contract?.mode;
        if (!mode || hand.extra.announcements !== null || belotCurrentTrick(hand).index !== 0)
            return hand.extra;
        if (!config.declarations)
            return { ...hand.extra, announcements: [] };
        const result = resolveBelotDeclarations(belotCardsDealt(hand.tricks, hand.secret.holdings), mode, table);
        return { ...hand.extra, announcements: announcementsOf(result) };
    },
    nextLeader: (_hand, winner) => winner,
    scoreHand: belotScoreHand,
    onPassedOut: () => ({ kind: 'redeal' }),
    matchEnd: (state, config) => belotMatchEnd(state, config),
    roles(_hand, table) {
        const all = Array.from({ length: table.seatCount }, (_, i) => i);
        return { activeSeats: all, controllerOf: all };
    },
    visibility: belotVisibility,
    contractLabel(contract) {
        return contract ? contract.label : 'Passed out';
    },
};
//# sourceMappingURL=module.js.map